/**
 * /journey — Target Journey page.
 *
 * Hero header tells the story in one line. Below that the circuit lights up
 * cascading from inputs to target. A "what's flowing" / "what's stuck" split
 * gives the founder a punch list before they drill into any module.
 */
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ChevronRight,
  Flame,
  Sparkles,
  Trophy,
  Zap,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { SectionHeader } from '@/components/charts/SectionHeader';
import { useDashboard } from '@/hooks/useDashboardContext';
import { formatINRCompact, formatNumberCompact } from '@/lib/format';
import { cn } from '@/lib/utils';
import { CircuitCanvas } from './CircuitCanvas';
import { useTargetJourney, useJourneyMoM, type JourneyScope, type MoMRow } from './hooks';
import type { JourneyNode } from './types';

export function TargetJourney() {
  const { outletId } = useDashboard();
  const outletScope = outletId === 'all' ? undefined : outletId;
  const [scope, setScope] = useState<JourneyScope>('this_month');
  const [view, setView] = useState<'journey' | 'mom'>('journey');
  const { data, isLoading } = useTargetJourney(outletScope, scope);

  const { flowing, stuck, headline } = useMemo(() => {
    if (!data) return { flowing: [], stuck: [], headline: '' };
    const nodes = Object.values(data.nodes);
    const flowing = nodes.filter((n) => n.status === 'good' && n.id !== 'target');
    const stuck = nodes.filter((n) => n.status === 'bad' || n.status === 'warn');
    const pct = Math.round((data.target.projected / Math.max(data.target.target, 1)) * 100);
    const gap = data.target.target - data.target.projected;

    let headline: string;
    if (scope === 'last_month') {
      headline =
        pct >= 100
          ? `🟢 Last month: closed ${formatINRCompact(data.target.mtdNet)} on a ${formatINRCompact(data.target.target)} target — +${formatINRCompact(-gap)} over.`
          : pct >= 90
            ? `🟡 Last month: closed ${formatINRCompact(data.target.mtdNet)} — ${formatINRCompact(gap)} short of ${formatINRCompact(data.target.target)} (${pct}% of target).`
            : `🔴 Last month: closed ${formatINRCompact(data.target.mtdNet)} — ${formatINRCompact(gap)} short (${pct}% of target). The red lamps below are where it leaked.`;
    } else {
      headline =
        pct >= 100
          ? `🟢 On pace to clear ${formatINRCompact(data.target.target)} — projected ${formatINRCompact(data.target.projected)} (+${formatINRCompact(-gap)})`
          : pct >= 90
            ? `🟡 ${pct}% to ${formatINRCompact(data.target.target)} — need to close ${formatINRCompact(gap)} this month`
            : `🔴 ${pct}% to ${formatINRCompact(data.target.target)} — ${formatINRCompact(gap)} short. Fix the red lamps first.`;
    }
    return { flowing, stuck, headline };
  }, [data, scope]);

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <SectionHeader
          title={
            <span className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-violet-500" />
              Target Journey
              <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-violet-500/10 px-2 py-0.5 text-2xs font-medium text-violet-700 dark:text-violet-300">
                <Sparkles className="h-3 w-3" /> {scope === 'last_month' ? 'closed' : 'live'}
              </span>
            </span>
          }
          description="The wiring diagram from every input you control to the monthly net-sales target. Follow the current — when a lamp goes red, fix the lever that feeds it."
        />
        <div className="flex flex-wrap items-center gap-2">
          <ViewTabs value={view} onChange={setView} />
          {view === 'journey' && <ScopeToggle value={scope} onChange={setScope} />}
        </div>
      </div>

      {view === 'mom' ? (
        <MoMTable outletScope={outletScope} />
      ) : isLoading || !data ? (
        <JourneyLoader />
      ) : (
        <>
          {/* Hero story strip */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Card className="overflow-hidden border-border/60 bg-gradient-to-r from-violet-500/8 via-card to-card">
              <CardContent className="flex flex-wrap items-center gap-4 p-4 md:p-5">
                <div className="flex flex-1 flex-col gap-1">
                  <div className="text-2xs uppercase tracking-wide text-muted-foreground">
                    {scope === 'last_month' ? 'Last month — closed' : 'This month at a glance'}
                  </div>
                  <div className="text-lg font-semibold leading-tight md:text-xl">{headline}</div>
                </div>
                <div className="flex items-center gap-5 text-sm">
                  <Stat
                    label={scope === 'last_month' ? 'Closed' : 'MTD'}
                    value={formatINRCompact(data.target.mtdNet)}
                  />
                  {scope !== 'last_month' && (
                    <Stat
                      label="Projected"
                      value={formatINRCompact(data.target.projected)}
                      accent="violet"
                    />
                  )}
                  <Stat
                    label="Target"
                    value={formatINRCompact(data.target.target)}
                    accent="muted"
                  />
                  <Stat
                    label={scope === 'last_month' ? 'Gap' : 'Month elapsed'}
                    value={
                      scope === 'last_month'
                        ? formatINRCompact(data.target.target - data.target.mtdNet)
                        : `${Math.round(data.target.monthElapsedPct * 100)}%`
                    }
                    accent={
                      scope === 'last_month' && data.target.target - data.target.mtdNet > 0
                        ? 'rose'
                        : 'muted'
                    }
                  />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <CircuitCanvas state={data} />

          {/* What's flowing / what's stuck */}
          <div className="grid gap-4 md:grid-cols-2">
            <DriverCard
              icon={<Trophy className="h-4 w-4 text-emerald-500" />}
              title="What's flowing"
              empty="Nothing lit up yet — start by activating an input."
              items={flowing}
              tone="good"
            />
            <DriverCard
              icon={<Flame className="h-4 w-4 text-rose-500" />}
              title="Where to push this week"
              empty="Every lamp is green. Hold the line and double-down on what's working."
              items={stuck}
              tone="bad"
            />
          </div>

          {/* Footer storytelling bar */}
          <Card className="border-dashed border-border/60 bg-muted/30">
            <CardContent className="flex items-start gap-3 p-4 text-2xs text-muted-foreground md:text-xs">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
              <span>
                Edges show <span className="font-semibold">% contribution</span> and the
                rupee flow (thicker = bigger). A blocked segment names the lever costing
                you the most this month. Click any node to dive into its source module —
                Sales, Aggregators, Meta, Reviews, Influencers.
              </span>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: 'violet' | 'muted' | 'rose';
}) {
  return (
    <div className="flex flex-col items-end leading-tight">
      <div
        className={cn(
          'text-base font-semibold tabular-nums md:text-lg',
          accent === 'violet' && 'text-violet-600 dark:text-violet-400',
          accent === 'muted' && 'text-muted-foreground',
          accent === 'rose' && 'text-rose-600 dark:text-rose-400',
        )}
      >
        {value}
      </div>
      <div className="text-2xs uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  );
}

function ScopeToggle({
  value,
  onChange,
}: {
  value: JourneyScope;
  onChange: (s: JourneyScope) => void;
}) {
  return (
    <div className="inline-flex rounded-md border border-border/60 bg-card p-0.5 text-xs">
      <button
        type="button"
        onClick={() => onChange('this_month')}
        className={cn(
          'rounded px-2.5 py-1.5 transition-colors',
          value === 'this_month'
            ? 'bg-background text-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground',
        )}
      >
        This month
      </button>
      <button
        type="button"
        onClick={() => onChange('last_month')}
        className={cn(
          'rounded px-2.5 py-1.5 transition-colors',
          value === 'last_month'
            ? 'bg-background text-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground',
        )}
      >
        Last month
      </button>
    </div>
  );
}

function ViewTabs({
  value,
  onChange,
}: {
  value: 'journey' | 'mom';
  onChange: (v: 'journey' | 'mom') => void;
}) {
  return (
    <div className="inline-flex rounded-md border border-border/60 bg-card p-0.5 text-xs">
      {(['journey', 'mom'] as const).map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          className={cn(
            'rounded px-2.5 py-1.5 transition-colors',
            value === v
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {v === 'journey' ? 'Journey' : 'MoM'}
        </button>
      ))}
    </div>
  );
}

function fmtMoM(v: number, kind: MoMRow['kind']): string {
  if (kind === 'inr') return v > 0 ? formatINRCompact(v) : '—';
  if (kind === 'pct') return `${v.toFixed(1)}%`;
  if (kind === 'rating') return v > 0 ? v.toFixed(2) : '—';
  return v > 0 ? formatNumberCompact(v) : '—';
}

function MoMTable({ outletScope }: { outletScope?: string }) {
  const { rows, isLoading } = useJourneyMoM(outletScope);
  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/60 text-left text-2xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3 font-medium">Metric</th>
                <th className="px-4 py-3 text-right font-medium">This month (MTD)</th>
                <th className="px-4 py-3 text-right font-medium">Last month</th>
                <th className="px-4 py-3 text-right font-medium">Change</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">
                    No comparison data for these months.
                  </td>
                </tr>
              ) : (
                rows.map((r) => {
                  const deltaPct =
                    r.lastMonth !== 0
                      ? ((r.thisMonth - r.lastMonth) / Math.abs(r.lastMonth)) * 100
                      : null;
                  const positive =
                    deltaPct == null ? null : r.invert ? deltaPct < 0 : deltaPct > 0;
                  return (
                    <tr key={r.key} className="border-b border-border/40 last:border-0">
                      <td className="px-4 py-2.5 font-medium">{r.label}</td>
                      <td className="px-4 py-2.5 text-right font-mono tabular-nums">
                        {fmtMoM(r.thisMonth, r.kind)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono tabular-nums text-muted-foreground">
                        {fmtMoM(r.lastMonth, r.kind)}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {deltaPct == null || Math.abs(deltaPct) < 0.05 ? (
                          <span className="font-mono text-2xs text-muted-foreground">—</span>
                        ) : (
                          <span
                            className={cn(
                              'font-mono text-xs font-semibold',
                              positive
                                ? 'text-emerald-600 dark:text-emerald-400'
                                : 'text-rose-600 dark:text-rose-400',
                            )}
                          >
                            {deltaPct > 0 ? '+' : ''}
                            {deltaPct.toFixed(1)}%
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
      <div className="px-4 py-2.5 text-2xs text-muted-foreground">
        This month is month-to-date; last month is the full month. Change is vs last month.
      </div>
    </Card>
  );
}

function DriverCard({
  icon,
  title,
  items,
  empty,
  tone,
}: {
  icon: React.ReactNode;
  title: string;
  items: JourneyNode[];
  empty: string;
  tone: 'good' | 'bad';
}) {
  const navigate = useNavigate();
  return (
    <Card
      className={cn(
        'overflow-hidden',
        tone === 'good' && 'border-emerald-500/20',
        tone === 'bad' && 'border-rose-500/20',
      )}
    >
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {icon}
          {title}
          <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-2xs font-medium text-foreground">
            {items.length}
          </span>
        </div>
        {items.length === 0 ? (
          <div className="rounded-md border border-dashed border-border/60 bg-muted/30 p-3 text-2xs text-muted-foreground">
            {empty}
          </div>
        ) : (
          <ul className="space-y-1.5">
            {items.map((n, i) => (
              <motion.li
                key={n.id}
                initial={{ opacity: 0, x: tone === 'bad' ? 8 : -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.04, duration: 0.25 }}
                className={cn(
                  'flex items-center justify-between gap-3 rounded-md border border-border/60 bg-card px-3 py-2 transition',
                  n.href && 'cursor-pointer hover:border-foreground/30 hover:bg-accent/40',
                )}
                onClick={() => n.href && navigate(n.href)}
                role={n.href ? 'button' : undefined}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Lamp tone={n.status === 'good' ? 'good' : n.status === 'warn' ? 'warn' : 'bad'} />
                    <span className="truncate text-sm font-medium">{n.label}</span>
                  </div>
                  {n.sub && (
                    <div className="ml-4 truncate text-2xs text-muted-foreground">{n.sub}</div>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="text-sm font-semibold tabular-nums">{n.value}</span>
                  {n.href && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                </div>
              </motion.li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function Lamp({ tone }: { tone: 'good' | 'warn' | 'bad' }) {
  return (
    <span
      className={cn(
        'inline-block h-2 w-2 rounded-full',
        tone === 'good' && 'bg-emerald-500',
        tone === 'warn' && 'bg-amber-500',
        tone === 'bad' && 'bg-rose-500',
      )}
    />
  );
}

// ───────── Journey loader ─────────
// Themed loader: pulsing zap with a circuit-board background and a rotating
// status line. Gives the operator something to read while the queries land.

const LOADER_LINES = [
  'Energising the circuit…',
  'Polling Petpooja for MTD net sales…',
  'Asking Swiggy + Zomato about the funnel…',
  'Reading add-on lift from today’s receipts…',
  'Counting walk-ins vs aggregator orders…',
  'Lighting up the lamps…',
  'Routing current to the target bulb…',
];

function JourneyLoader() {
  const [lineIdx, setLineIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(
      () => setLineIdx((i) => (i + 1) % LOADER_LINES.length),
      1400,
    );
    return () => clearInterval(id);
  }, []);
  return (
    <div className="relative overflow-hidden rounded-xl border border-border/40 bg-gradient-to-br from-card/60 via-card/40 to-violet-500/5 p-10">
      {/* Faint circuit grid in the background */}
      <svg
        className="absolute inset-0 h-full w-full opacity-[0.06]"
        viewBox="0 0 200 200"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden
      >
        <defs>
          <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="currentColor" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="200" height="200" fill="url(#grid)" />
      </svg>

      <div className="relative flex flex-col items-center justify-center gap-5 py-10">
        {/* Pulsing zap with concentric rings */}
        <div className="relative h-20 w-20">
          {[0, 1, 2].map((i) => (
            <motion.span
              key={i}
              className="absolute inset-0 rounded-full border border-violet-400/40"
              initial={{ scale: 0.6, opacity: 0.8 }}
              animate={{ scale: 1.8, opacity: 0 }}
              transition={{
                duration: 1.8,
                repeat: Infinity,
                ease: 'easeOut',
                delay: i * 0.6,
              }}
            />
          ))}
          <motion.div
            className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-violet-500/30 to-violet-500/5 ring-1 ring-violet-400/50"
            animate={{ rotate: [0, 6, -6, 0] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Zap className="h-8 w-8 text-violet-300 drop-shadow-[0_0_14px_rgba(167,139,250,0.65)]" />
          </motion.div>
        </div>

        <div className="text-center">
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
            Target Journey
          </div>
          <motion.div
            key={lineIdx}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="mt-2 text-sm font-medium tabular-nums"
          >
            {LOADER_LINES[lineIdx]}
          </motion.div>
        </div>

        {/* Animated current dots tracing a horizontal wire */}
        <svg width="260" height="14" viewBox="0 0 260 14" aria-hidden>
          <path
            id="loader-wire"
            d="M 5 7 L 255 7"
            stroke="rgba(167,139,250,0.35)"
            strokeWidth="1.2"
            strokeDasharray="3 4"
            fill="none"
          />
          {[0, 1, 2].map((i) => (
            <circle key={i} r="2.4" fill="#a78bfa">
              <animateMotion dur="1.8s" repeatCount="indefinite" begin={`${i * 0.6}s`}>
                <mpath href="#loader-wire" />
              </animateMotion>
            </circle>
          ))}
        </svg>
      </div>
    </div>
  );
}
